import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { getYearsFromToCurrent } from "../../helpers/common";
import { SelectDropdown } from "../SelectDropdown";
import Drawer from "../Drawer";
import { RevenueInputForecastDemand } from "./forecastDemandTableComponents/RevenueInputForecastDemand";
import { toast } from "react-toastify";

const FORM_ID = "forecast-demand-planning-sidebar-form";

const emptyDraft = () => ({
    name: "",
    notes: "",
    forecastYear: "",
    revenueTarget: 0,
    publishStatus: "DRAFT",
});

const STATUS_OPTIONS = [
    { value: "DRAFT", label: "Draft" },
    { value: "PUBLISHED", label: "Published" },
];

const ForecastDemandPlanningSidebar = ({
    isOpen,
    onClose,
    mode,
    record,
    isSaving,
    onSubmit,
}) => {
    const [draft, setDraft] = useState(emptyDraft());
    const yearOptions = getYearsFromToCurrent(2024);

    useEffect(() => {
        if (!isOpen) return;
        if (mode === "edit" && record) {
            setDraft({
                name: record.name ?? "",
                notes: record.notes ?? "",
                forecastYear:
                    record.forecastYear != null && record.forecastYear !== ""
                        ? String(record.forecastYear)
                        : "",
                revenueTarget:
                    record.revenueTarget != null && record.revenueTarget !== ""
                        ? Number(record.revenueTarget)
                        : 0,
                publishStatus: record.publishStatus === "PUBLISHED" ? "PUBLISHED" : "DRAFT",
            });
        } else {
            setDraft(emptyDraft());
        }
    }, [isOpen, mode, record]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const fy = draft.forecastYear.trim();
        const rt = draft.revenueTarget;
        const name = draft.name.trim() || null;
        const notes = draft.notes.trim() || null;
        if (!name) {
            toast.error("Name is required", { position: "bottom-right" });
            return;
        }
        if (!fy) {
            toast.error("Forecast year is required", { position: "bottom-right" });
            return;
        }
        onSubmit({
            name: name,
            notes: notes,
            forecastYear: fy === "" ? null : fy,
            revenueTarget:
                rt === null || rt === undefined || Number(rt) === 0 || Number.isNaN(Number(rt))
                    ? null
                    : Number(rt),
            ...(mode === "edit" ? { publishStatus: draft.publishStatus } : {}),
        });
    };

    const title =
        mode === "create" ? "Create demand planning" : "Edit demand planning";

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            width="480px"
            formId={FORM_ID}
            showActionButtons
            submitButtonText={mode === "create" ? "Create" : "Save changes"}
            submitDisabled={isSaving}
        >
            <form id={FORM_ID} onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fdp-name">
                        Name
                    </label>
                    <input
                        id="fdp-name"
                        type="text"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        autoComplete="off"
                    />
                </div>
                <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                        Forecast year
                    </p>
                    <SelectDropdown
                        options={yearOptions}
                        value={draft.forecastYear || undefined}
                        portal
                        placeholder="Select forecast year"
                        onChange={(v) =>
                            setDraft((d) => ({
                                ...d,
                                forecastYear: v != null && v !== "" ? String(v) : "",
                            }))
                        }
                        selectedValueClassname="!text-sm !text-gray-900"
                        itemValueClassname="!py-2 !px-3 !text-sm !text-gray-900"
                        selectClassname="!rounded-md !px-3 !py-2 !text-sm !bg-white !border-gray-300 hover:!bg-gray-50 !shadow-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Revenue target
                    </label>
                    <RevenueInputForecastDemand
                        value={draft.revenueTarget}
                        neutralStyle
                        onSave={(num) =>
                            setDraft((d) => ({ ...d, revenueTarget: num }))
                        }
                    />
                    <p className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-1">
                        <Info size={10} aria-hidden /> Manual input — all downstream figures derive from this
                    </p>
                </div>
                {mode === "edit" && (
                    <div>
                        <p className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </p>
                        <SelectDropdown
                            options={STATUS_OPTIONS}
                            value={draft.publishStatus}
                            portal
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    publishStatus: v === "PUBLISHED" ? "PUBLISHED" : "DRAFT",
                                }))
                            }
                            selectedValueClassname="!text-sm !text-gray-900"
                            itemValueClassname="!py-2 !px-3 !text-sm !text-gray-900"
                            selectClassname="!rounded-md !px-3 !py-2 !text-sm !bg-white !border-gray-300 hover:!bg-gray-50 !shadow-sm"
                        />
                        <p className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-1">
                            <Info size={10} aria-hidden /> Only one plan can be Published at a time — publishing this plan sets all others to Draft
                        </p>
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fdp-notes">
                        Notes
                    </label>
                    <textarea
                        id="fdp-notes"
                        rows={4}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        value={draft.notes}
                        onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    />
                </div>
            </form>
        </Drawer>
    );
};

export default ForecastDemandPlanningSidebar;
